<template>
    <ExternalUserActionComponent
        dialog-id="externalUserActionRecommendationsDialog"
        open-dialog-event="external-action:recommendations:add"
        updated-event="external-action:recommendations:added"
        :confirm-function="update"
        :has-error="hasError"
        :open-dialog-post-function="openDialogPostFunction"
        confirm-data-testid="public-reco-send"
    >
        <template #edit-header>
            {{ t('profile.section.recommendation.on.invitation.title', {0: profile?.firstname}) }}
        </template>
        <template #edit-body>
            <VJoyWrapper direction="column" align="stretch">
                <div>
                    <VJoyInput
                        v-model="r$.$value.authorCompanyName"
                        name="companyName"
                        :label="t('profile.xp.reco.form.company.label')"
                        data-testid="public-reco-company"
                    />
                    <VJoyFormError v-for="error of r$.authorCompanyName.$errors" :key="error" no-margin class="error-message">
                        {{ error }}
                    </VJoyFormError>
                </div>
                <div>
                    <VJoyInput
                        v-model="r$.$value.recommenderJob"
                        name="recommenderJob"
                        :label="t('profile.xp.reco.form.job.label')"
                        data-testid="public-reco-job"
                    />
                    <VJoyFormError v-for="error of r$.recommenderJob.$errors" :key="error" no-margin class="error-message">
                        {{ error }}
                    </VJoyFormError>
                </div>
                <div v-if="experiences">
                    <ExperienceSelector v-if="experiences.items.length" v-model="r$.$value.experienceId" :experiences="experiences.items" />
                </div>
                <div>
                    <VJoyTextarea
                        v-model="r$.$value.text"
                        :label="t('profile.section.recommendation.edit.comment.label')"
                        data-testid="public-reco-text"
                    />
                    <VJoyFormError v-for="error of r$.text.$errors" :key="error" no-margin class="error-message">
                        {{ error }}
                    </VJoyFormError>
                </div>
            </VJoyWrapper>
        </template>
    </ExternalUserActionComponent>
</template>

<script setup lang="ts">
    import {onMounted, ref} from 'vue';
    import {required} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {computed, useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {pushVJoySnackbar, VJoyFormError, VJoyInput, VJoyTextarea, VJoyWrapper} from '@maltjoy/core-vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
    import ExperienceSelector from '@/components/edit/recommendations/ui/ExperienceSelector.vue';
    import ExternalUserActionComponent from '@/components/external-user-action/ExternalUserActionComponent.vue';
    import {eventBus} from '@/config/events';
    import {useProfileStore} from '@/store/profileStore';

    const {t} = useTranslation();
    const recommendationsStore = useRecommendationsStore();
    const {profile, experiences} = storeToRefs(useProfileStore());
    const {trackRecommendationLeft} = useProfileTracking();

    const rules = computed(() => ({
        authorCompanyName: {
            required,
        },
        recommenderJob: {
            required,
        },
        text: {
            required,
        },
        experienceId: {},
    }));
    const defaultState = computed(() => ({
        authorCompanyName: '',
        recommenderJob: '',
        text: '',
        experienceId: '',
    }));
    const state = ref({...defaultState.value});
    const {r$} = useRegle(state, rules);

    const hasError = computed(() => {
        return r$.authorCompanyName.$invalid || r$.recommenderJob.$invalid || r$.text.$invalid;
    });
    async function update() {
        const {valid} = await validateForm(r$);
        if (!valid) {
            return;
        }

        try {
            const experienceId = state.value.experienceId.length > 0 ? state.value.experienceId : undefined;
            await recommendationsStore.sendRecommendation({
                profileId: profile.value!.profileId,
                authorCompanyName: state.value.authorCompanyName,
                recommenderJob: state.value.recommenderJob,
                text: state.value.text,
                experienceId,
            });
            trackRecommendationLeft(experienceId, profile.value!.profileId);
        } catch (error: any) {
            pushVJoySnackbar({
                props: {
                    message: t('generic.error.unexpected'),
                    level: 'error',
                },
            });

            throw error;
        }

        eventBus.emit('main:recommendations:scroll-to');
        eventBus.emit('external-action:recommendations:added');
    }

    function openDialogPostFunction(params: any) {
        if (params && params.experienceId) {
            state.value.experienceId = params.experienceId;
        }
    }

    onMounted(() => {
        eventBus.emit('external-action:recommendations:ready');
    });
</script>

<style scoped lang="scss">
    .error-message {
        margin-top: var(--joy-core-spacing-2) !important;
    }
</style>
